/**
 * The remainder of a PART-APPLIED deposit at checkout (tv-2na.8.17).
 *
 * DECIDED 24 Sep (Q1): the leftover FOLLOWS THE BOOKING (vault boards 02 / 07 / 10):
 *   - rebooked next session  -> part of the deposit is applied to today's balance and
 *     the REST ROLLS to the rebook (the existing rollover-out / rollover-in pair);
 *   - final session / one-off -> whatever is left is USED UP (no credit kept). It is
 *     written to the ledger as its own `redeemed` movement, so nothing is stranded;
 *   - mid-piece, no rebook    -> the rest stays ON FILE for 12 months
 *     (onFileExpiryFrom): a dateless paid deposit, which the Sweeper works
 *     (board 07/10 no-rebook rule).
 *
 * The bug this closes: the applied part was written as ONE `redeemed` entry on a
 * deposit that stayed linked to the finished session with its old expiry, and a
 * `redeemed` entry reads as "fully used" — so the leftover was on no one's list.
 *
 * Pure: the caller (the apply-deposit intent, and the web store fallback) writes
 * the plan. The original never has its ledger replaced — entries are APPENDED.
 */
import { ledgerPaidAud, onFileExpiryFrom, type CancelSplitDeposit } from './cancelSplit';

export type RemainderCase = 'rolls-to-rebook' | 'used-up' | 'on-file';

/** Days a rolled deposit's link stays valid (mirrors web sessionFlow buildRollover). */
const ROLL_EXPIRY_DAYS = 30;

/**
 * Which branch the checkout is on, from what the Floor already records:
 * the rebook outcome kind, whether the piece is complete, whether there is a piece.
 */
export function remainderCaseFor(input: {
  outcomeKind?: string;
  pieceComplete: boolean;
  hasPiece: boolean;
}): RemainderCase {
  if (input.pieceComplete) return 'used-up';
  if (input.outcomeKind === 'rebookedInChair') return 'rolls-to-rebook';
  if (!input.hasPiece) return 'used-up'; // a one-off: nothing to come back for
  return 'on-file';
}

/** A ledger movement (the web DepositLedgerEntry / db DepositLedgerRow shape). */
export interface RemainderLedgerEntry {
  at: string;
  amountAud: number;
  kind: 'redeemed' | 'rollover-out' | 'rollover-in';
  by: string;
  note: string;
  fromBookingId?: string;
  toBookingId?: string;
  toPieceId?: string;
  counterpartDepositId?: string;
}

export interface DepositRemainderInput {
  originalDepositId: string;
  /** What is on the deposit now (ledger-derived paid balance). */
  paidAud: number;
  /** Applied to today's session balance. Clamped to [0, paidAud]. */
  appliedAud: number;
  remainderCase: RemainderCase;
  atIso: string;
  /** The session being checked out. */
  fromBookingId: string;
  /** Client-minted id for the deposit the remainder moves to (idempotency key). */
  remainderDepositId: string;
  /** rolls-to-rebook: the next session the remainder lands on. */
  toBookingId?: string;
  toPieceId?: string;
}

export interface DepositRemainderPlan {
  appliedAud: number;
  remainderAud: number;
  /** Entries to APPEND to the original deposit's ledger. */
  originalEntries: RemainderLedgerEntry[];
  /** The original's status after the plan ('rolled-over' once money has moved out). */
  originalStatus: 'paid' | 'rolled-over';
  /** The deposit the remainder moves to (rolls-to-rebook / on-file only). */
  remainder?: {
    id: string;
    amountAud: number;
    /** The next session (rolls-to-rebook); undefined = dateless, on file. */
    bookingId?: string;
    expiresAt: string;
    entry: RemainderLedgerEntry;
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Money still on a deposit to apply or carry: the paid balance (ledgerPaidAud)
 * less anything already `redeemed` against a session.
 */
export function availableToApplyAud(
  d: Pick<CancelSplitDeposit, 'ledger' | 'amountPaid' | 'status' | 'amountAud'>,
): number {
  const redeemed = (d.ledger ?? []).reduce((s, e) => (e.kind === 'redeemed' ? s + Number(e.amountAud) : s), 0);
  return Math.max(0, round2(ledgerPaidAud(d) - redeemed));
}

export function planDepositRemainder(input: DepositRemainderInput): DepositRemainderPlan {
  const paid = Math.max(0, round2(input.paidAud));
  const applied = Math.min(paid, Math.max(0, round2(input.appliedAud)));
  const remainder = round2(paid - applied);
  const at = input.atIso;
  const piece = input.toPieceId ? { toPieceId: input.toPieceId } : {};

  const originalEntries: RemainderLedgerEntry[] = [];
  if (applied > 0) {
    originalEntries.push({
      at,
      amountAud: applied,
      kind: 'redeemed',
      by: 'system',
      fromBookingId: input.fromBookingId,
      toBookingId: input.fromBookingId,
      ...piece,
      note: 'Deposit applied to the session balance',
    });
  }
  if (remainder <= 0) {
    return { appliedAud: applied, remainderAud: 0, originalEntries, originalStatus: 'paid' };
  }

  if (input.remainderCase === 'used-up') {
    originalEntries.push({
      at,
      amountAud: remainder,
      kind: 'redeemed',
      by: 'system',
      fromBookingId: input.fromBookingId,
      toBookingId: input.fromBookingId,
      ...piece,
      note: 'Remainder used up · final session / one-off (no credit kept)',
    });
    return { appliedAud: applied, remainderAud: remainder, originalEntries, originalStatus: 'paid' };
  }

  const rolls = input.remainderCase === 'rolls-to-rebook';
  const toBooking = rolls && input.toBookingId ? { toBookingId: input.toBookingId } : {};
  originalEntries.push({
    at,
    amountAud: remainder,
    kind: 'rollover-out',
    by: 'system',
    counterpartDepositId: input.remainderDepositId,
    fromBookingId: input.fromBookingId,
    ...toBooking,
    note: rolls ? 'Remainder rolled to next session' : 'Remainder kept on file (no date · 12 months)',
  });
  return {
    appliedAud: applied,
    remainderAud: remainder,
    originalEntries,
    originalStatus: 'rolled-over',
    remainder: {
      id: input.remainderDepositId,
      amountAud: remainder,
      ...(rolls && input.toBookingId ? { bookingId: input.toBookingId } : {}),
      expiresAt: rolls
        ? new Date(Date.parse(at) + ROLL_EXPIRY_DAYS * 86_400_000).toISOString()
        : onFileExpiryFrom(at),
      entry: {
        at,
        amountAud: remainder,
        kind: 'rollover-in',
        by: 'system',
        counterpartDepositId: input.originalDepositId,
        fromBookingId: input.fromBookingId,
        ...toBooking,
        note: rolls ? 'Remainder rolled from previous session' : 'Remainder on file from previous session',
      },
    },
  };
}
